import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Trans, t } from '@lingui/macro';
import { Outlet, useLocation } from 'react-router-dom';
import useQueryParams from 'hooks/useQueryParams';
import type { SearchableFormValues } from 'components/SearchableFilter';
import isEqual from 'lodash/isEqual';
import omit from 'lodash/omit';
import pick from 'lodash/pick';

import * as ManagementLayout from 'components/ManagementLayout';

import { useCurrentUserContext } from 'utils/User';
import useFeatureFlag, { FEATURE_FLAGS } from 'utils/useFeatureFlag';

import { TabButton } from './components/TabButton';

import OemSegments from './OemSegments';
import Teams from './Teams';
import Fleets from './Fleets';
import Depots from './Depots';

import {
  ManagementPageContext,
  ManagementPageContextType,
} from './contexts/ManagementPageContext';

export type TabKey = 'oem-segments' | 'teams' | 'accounts' | 'depots';
type TabObject = Record<TabKey, { label: string; value: React.ComponentType }>;

export interface ManagementQueryParams {
  tab?: TabKey;
  oemSegmentIds: string[];
  teamIds: string[];
  accountIds: string[];
  depotIds: string[];
}

function getTabObject(tabKey: TabKey) {
  const TabObjects: TabObject = {
    'oem-segments': { label: t`OEM Segments`, value: OemSegments },
    teams: { label: t`Teams`, value: Teams },
    accounts: { label: t`Accounts`, value: Fleets },
    depots: { label: t`Depots`, value: Depots },
  };

  return TabObjects[tabKey];
}

interface GenerateInitialTabParams {
  showOemSegmentsTab: boolean;
  showTeamsTab: boolean;
}

function generateInitialTab({
  showOemSegmentsTab,
  showTeamsTab,
}: GenerateInitialTabParams) {
  if (showOemSegmentsTab) return 'oem-segments';
  if (showTeamsTab) return 'teams';

  return 'accounts';
}

const INITIAL_FILTERS: ManagementQueryParams = {
  tab: undefined,
  oemSegmentIds: [],
  teamIds: [],
  accountIds: [],
  depotIds: [],
};

const INITIAL_ROUTE_FILTERS = {
  oemSegmentIds: [],
  teamIds: [],
  accountIds: [],
  depotIds: [],
};

const FILTER_KEYS = Object.keys(INITIAL_ROUTE_FILTERS);

function getSectionLabel(pathname: string): string {
  if (pathname.endsWith('/oem-segments')) return t`OEM Segments`;
  if (pathname.endsWith('/teams')) return t`Teams`;
  if (pathname.endsWith('/accounts')) return t`Accounts`;
  if (pathname.endsWith('/depots')) return t`Depots`;
  return '';
}

const ManagementPage = () => {
  const pd1188FFEnabled = useFeatureFlag(
    FEATURE_FLAGS.PD_1188_ROUTE_BASED_FLEET_MANAGEMENT,
  );

  const location = useLocation();

  const { isSuperUser, isOemManager, oemSegments, managedSentinelTeams } =
    useCurrentUserContext();

  /* istanbul ignore next -- @preserve */
  const oemSegmentsLength = oemSegments?.length ?? 0;
  const hasMultipleOemSegments = isOemManager && oemSegmentsLength > 1;
  /* istanbul ignore next -- @preserve */
  const showOemSegmentsTab = (isSuperUser || hasMultipleOemSegments) ?? false;

  /* istanbul ignore next -- @preserve */
  const amountOfManagedTeams = managedSentinelTeams?.length ?? 0;
  const showTeamsTab = isSuperUser || amountOfManagedTeams > 1;
  const initialTab = generateInitialTab({ showOemSegmentsTab, showTeamsTab });

  const [pageParams, setPageParams, resetPageParams] =
    useQueryParams<ManagementQueryParams>(
      'management-page',
      pd1188FFEnabled ? INITIAL_ROUTE_FILTERS : INITIAL_FILTERS,
      {
        // Type coercion is disabled to prevent automatic conversion of array params to integers
        // This ensures the IDs remain as strings, matching their GraphQL types.
        typeCoercion: false,
      },
    );

  // Reset tab state when React Router navigates to /management without a tab param.
  // useStorage only syncs URL→state on popstate (back/forward), not on programmatic navigation.
  const locationKeyRef = useRef(location.key);
  /* istanbul ignore next -- @preserve */
  useEffect(() => {
    if (pd1188FFEnabled) return;
    if (locationKeyRef.current === location.key) return;
    locationKeyRef.current = location.key;

    const searchParams = new URLSearchParams(location.search);
    if (!searchParams.has('tab')) {
      resetPageParams();
    }
  }, [pd1188FFEnabled, location.key, location.search, resetPageParams]);

  const isFiltersDirty = useMemo(
    () => !isEqual(INITIAL_ROUTE_FILTERS, pick(pageParams, FILTER_KEYS)),
    [pageParams],
  );

  /* istanbul ignore next -- @preserve */
  const selectedTab = (pageParams.tab || initialTab) as TabKey;

  const [tabResetCounter, setTabResetCounter] = useState(0);

  const handleTabChange = useCallback(
    (tab: TabKey) => () => {
      const url = new URL(window.location.href);
      ['sort', 'sortDirection', 'page', 'searchText'].forEach((param) =>
        url.searchParams.delete(param),
      );
      window.history.replaceState({}, '', url);
      setTabResetCounter((c) => c + 1);
      setPageParams((oldState) => ({ ...oldState, tab }));
    },
    [setPageParams],
  );

  const tabObject = useMemo(
    () => (!pd1188FFEnabled ? getTabObject(selectedTab) : null),
    [pd1188FFEnabled, selectedTab],
  );

  /* istanbul ignore next -- @preserve */
  const handleChangeOemSegmentsFilter = useCallback(
    ({ value }: SearchableFormValues) => {
      setPageParams((oldState) => ({
        ...oldState,
        oemSegmentIds: value as string[],
        teamIds: [],
        accountIds: [],
        depotIds: [],
      }));
    },
    [setPageParams],
  );

  /* istanbul ignore next -- @preserve */
  const handleChangeTeamsFilter = useCallback(
    ({ value }: SearchableFormValues) => {
      setPageParams((oldState) => ({
        ...oldState,
        teamIds: value as string[],
        accountIds: [],
        depotIds: [],
      }));
    },
    [setPageParams],
  );

  /* istanbul ignore next -- @preserve */
  const handleChangeAccountsFilter = useCallback(
    ({ value }: SearchableFormValues) => {
      setPageParams((oldState) => ({
        ...oldState,
        accountIds: value as string[],
        depotIds: [],
      }));
    },
    [setPageParams],
  );

  /* istanbul ignore next -- @preserve */
  const handleChangeDepotsFilter = useCallback(
    ({ value }: SearchableFormValues) => {
      setPageParams((oldState) => ({
        ...oldState,
        depotIds: value as string[],
      }));
    },
    [setPageParams],
  );

  /* istanbul ignore next -- @preserve */
  const handleClearFilters = useCallback(() => {
    setPageParams((oldState) => ({
      ...oldState,
      ...(pd1188FFEnabled
        ? INITIAL_ROUTE_FILTERS
        : omit(INITIAL_FILTERS, 'tab')),
    }));
  }, [pd1188FFEnabled, setPageParams]);

  const values = useMemo(
    () =>
      ({
        isFiltersDirty,
        pageParams,
        setPageParams,
        handleChangeOemSegmentsFilter,
        handleChangeTeamsFilter,
        handleChangeAccountsFilter,
        handleChangeDepotsFilter,
        handleClearFilters,
      } satisfies ManagementPageContextType),
    [
      isFiltersDirty,
      pageParams,
      setPageParams,
      handleChangeOemSegmentsFilter,
      handleChangeTeamsFilter,
      handleChangeAccountsFilter,
      handleChangeDepotsFilter,
      handleClearFilters,
    ],
  );

  if (pd1188FFEnabled) {
    const sectionLabel = getSectionLabel(location.pathname);
    return (
      <ManagementPageContext.Provider value={values}>
        <ManagementLayout.Root>
          <ManagementLayout.Header.Container>
            <ManagementLayout.Header.Left>
              <ManagementLayout.Header.Title>
                {sectionLabel ? `${sectionLabel}` : <Trans>Management</Trans>}
              </ManagementLayout.Header.Title>
            </ManagementLayout.Header.Left>
          </ManagementLayout.Header.Container>
          <Outlet />
        </ManagementLayout.Root>
      </ManagementPageContext.Provider>
    );
  }

  /* istanbul ignore next -- @preserve */
  const { label, value: SelectedTabComponent } =
    tabObject ?? getTabObject(selectedTab);

  return (
    <ManagementPageContext.Provider value={values}>
      <ManagementLayout.Root>
        <ManagementLayout.Header.Container>
          <ManagementLayout.Header.Left>
            <ManagementLayout.Header.Title>
              <Trans>Management</Trans> / {label}
            </ManagementLayout.Header.Title>
          </ManagementLayout.Header.Left>
        </ManagementLayout.Header.Container>
        <ManagementLayout.EntityTabsContainer>
          {showOemSegmentsTab && (
            <TabButton
              text={t`OEM Segments`}
              onClick={handleTabChange('oem-segments')}
              active={selectedTab === 'oem-segments'}
            />
          )}
          {showTeamsTab && (
            <TabButton
              text={t`Teams`}
              onClick={handleTabChange('teams')}
              active={selectedTab === 'teams'}
            />
          )}
          <TabButton
            text={t`Accounts`}
            onClick={handleTabChange('accounts')}
            active={selectedTab === 'accounts'}
          />
          <TabButton
            text={t`Depots`}
            onClick={handleTabChange('depots')}
            active={selectedTab === 'depots'}
          />
        </ManagementLayout.EntityTabsContainer>
        <SelectedTabComponent key={`${selectedTab}-${tabResetCounter}`} />
      </ManagementLayout.Root>
    </ManagementPageContext.Provider>
  );
};

export default ManagementPage;
